import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Expand } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { MaskLines, Reveal } from "@/components/site/Reveal";
import { CtaBand } from "@/components/site/CtaBand";
import { Lightbox } from "@/components/site/Lightbox";
import { VideoEmbed } from "@/components/site/VideoEmbed";
import { PROJECTS } from "@/data/projects";
import { areaByName } from "@/data/areas";

const ProjectGallery = ({ p, n }) => {
  const [idx, setIdx] = useState(null);
  const area = areaByName(p.suburb);
  return (
    <section id={p.id} data-testid={`gallery-${p.id}`} className="scroll-mt-24 border-t border-ink/15 pt-12">
      <div className="grid gap-6 lg:grid-cols-12 items-end mb-8">
        <div className="lg:col-span-7">
          <p className="eyebrow text-cobalt mb-4">{String(n).padStart(2, "0")} · {p.gallery.length} photo{p.gallery.length > 1 ? "s" : ""}</p>
          <h2 className="font-wide font-black uppercase text-3xl sm:text-5xl tracking-tight leading-[0.95]"><MaskLines lines={[p.title]} /></h2>
        </div>
        <Reveal className="lg:col-span-5 space-y-3">
          <p className="text-slate-700 leading-relaxed">{p.note}</p>
          <p className="text-xs font-mono text-slate-500">Plumbing by Austyle Plumbing{p.credits ? ` · ${p.credits}` : ""}</p>
          {area && <Link to={`/areas/${area.slug}`} data-testid={`gallery-area-${p.id}`} className="inline-flex items-center gap-1.5 text-sm font-bold text-cobalt link-underline"><MapPin className="h-4 w-4" />Plumber in {p.suburb}</Link>}
        </Reveal>
      </div>
      <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {p.gallery.map((src, i) => (
          <motion.button key={src} type="button" onClick={() => setIdx(i)} data-testid={`gallery-${p.id}-item-${i}`}
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: 0.6, delay: (i % 4) * 0.06 }}
            className={`group relative aspect-[4/5] overflow-hidden bg-ink ${p.gallery.length === 2 ? "col-span-2 md:col-span-3 lg:col-span-2 !aspect-[4/3]" : i === 0 ? "col-span-2 row-span-2 aspect-auto min-h-[320px]" : p.gallery.length === 3 ? "lg:row-span-2 lg:aspect-auto" : ""}`}>
            <img src={src} alt={`${p.title} – photo ${i + 1}`} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]" />
            <span className="absolute inset-0 bg-ink/0 group-hover:bg-ink/30 transition-colors" />
            <Expand className="absolute bottom-3 right-3 h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </motion.button>
        ))}
      </div>
      {p.video && <VideoEmbed video={p.video} testid={`video-${p.id}`} />}
      <Lightbox images={p.gallery} index={idx} title={p.title} onClose={() => setIdx(null)} onIndex={setIdx} />
    </section>
  );
};

export default function Projects() {
  return (
    <>
      <Seo title="Plumbing Projects Sydney | Austyle Plumbing" description="Luxury Sydney homes where Austyle Plumbing completed the plumbing – bathrooms, kitchens and new homes in Padstow, Hurstville, Connells Point, Panania, Cronulla and more." />
      <PageHero testid="projects-hero" eyebrow="Plumbing projects" lines={["Our plumbing.", "Finished homes."]} image="/images/ig/p7.jpg" pos="center 40%"
        intro="Homes across Sydney where we completed the plumbing, working alongside the builders and designers credited on each project. Tap any photo to view it full screen." />
      <div className="bg-porcelain sticky top-[64px] z-30 border-b border-ink/10 hidden md:block">
        <div className="container-x flex gap-2 py-3 overflow-x-auto">
          {PROJECTS.map((p) => (
            <a key={p.id} href={`#${p.id}`} data-testid={`projects-jump-${p.id}`} className="shrink-0 h-9 px-4 rounded-full border border-ink/15 text-xs font-bold uppercase flex items-center hover:bg-ink hover:text-white transition-colors">{p.title}</a>
          ))}
        </div>
      </div>
      <div className="bg-porcelain py-16 sm:py-24">
        <div className="container-x space-y-20">
          {PROJECTS.map((p, i) => <ProjectGallery key={p.id} p={p} n={i + 1} />)}
        </div>
      </div>
      <CtaBand title={["Want this finish", "in your home?"]} />
    </>
  );
}
